import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listarDivergenciasDeSaldo } from '../api/divergencias-saldo.js';
import type {
  DecisaoDaDivergenciaDeSaldo,
  DivergenciaDeSaldo,
  FiltroDeDivergenciasDeSaldo,
} from '../api/divergencias-saldo.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../api/paginacao.js';
import { seInformado } from '../api/recorte.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePendenciasDeSaldo } from '../app/pendencias-de-saldo.js';
import { usePode } from '../autorizacao/usePode.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import { Botao } from '../componentes/Botao.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import { formatarDataHora } from '../componentes/datas.js';
import { filialDoFiltro, rotuloDoLocal } from '../componentes/deposito.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../componentes/FaixaDeFiltros.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroSelecao,
} from '../componentes/Filtros.js';
import { IconeConfere } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Paginacao } from '../componentes/Paginacao.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import { formatarQuantidade } from './movimentacoes/quantidade.js';
import { Quantidade } from './movimentacoes/SaldoDaAbertura.js';
import { useValorAdiado } from './movimentacoes/useValorAdiado.js';
import {
  formatarDiferenca,
  OPCOES_DE_STATUS,
  rotuloDoStatus,
  STATUS_PADRAO,
  statusDoFiltro,
  tomDoStatus,
} from './saldos/divergencia-de-saldo.js';
import {
  DialogoDeDecisaoDeSaldo,
  rotuloDaDecisaoDeSaldo,
} from './saldos/DialogoDeDecisaoDeSaldo.js';
import './DivergenciasDeSaldo.css';

type Pedido = {
  readonly divergencia: DivergenciaDeSaldo;
  readonly decisao: DecisaoDaDivergenciaDeSaldo;
};

/** A pendente espera gente — não pode se parecer com o histórico. */
function classeDaLinha(divergencia: DivergenciaDeSaldo): string | undefined {
  return divergencia.status === 'Pendente' ? 'saldos__linha--pendente' : undefined;
}

function localDa(divergencia: DivergenciaDeSaldo): string {
  return rotuloDoLocal(divergencia.codFil, divergencia.codDep);
}

/** A frase do aviso depois da decisão, com o número que passou a valer. */
function avisoDaDecisao(decidida: DivergenciaDeSaldo): string {
  const alvo = `Saldo de ${decidida.codPro} em ${localDa(decidida)}`;
  return decidida.status === 'Atualizada'
    ? `${alvo} atualizado para ${formatarQuantidade(decidida.quantidadeDisponivelErp)}, como está no ERP.`
    : `${alvo} mantido em ${formatarQuantidade(decidida.quantidadeDisponivelLocal)}.`;
}

function ProdutoDaDivergencia({ divergencia }: { readonly divergencia: DivergenciaDeSaldo }) {
  const { produto } = divergencia;
  return (
    <span className="saldos__produto">
      <span className="saldos__codigo">
        {divergencia.codPro}
        {divergencia.codDer !== '' && (
          <span className="saldos__derivacao">
            {' '}
            {divergencia.codDer}
            {produto.descricaoDerivacao !== null && ` · ${produto.descricaoDerivacao}`}
          </span>
        )}
      </span>
      <span className="saldos__discreto">
        {produto.nome} · {produto.unidadeMedida}
      </span>
    </span>
  );
}

function LocalDaDivergencia({ divergencia }: { readonly divergencia: DivergenciaDeSaldo }) {
  return (
    <span className="saldos__produto">
      <span className="mono">{localDa(divergencia)}</span>
      {divergencia.codLot !== '' && (
        <span className="saldos__discreto">Lote {divergencia.codLot}</span>
      )}
      {divergencia.numSep !== '' && (
        <span className="saldos__discreto">Série {divergencia.numSep}</span>
      )}
    </span>
  );
}

function DatasDaDivergencia({ divergencia }: { readonly divergencia: DivergenciaDeSaldo }) {
  return (
    <dl className="saldos__datas">
      <div>
        <dt>Detectada</dt>
        <dd>{formatarDataHora(divergencia.detectadaEm)}</dd>
      </div>
      <div>
        <dt>Lida do ERP</dt>
        <dd>{formatarDataHora(divergencia.sincronizadoEm)}</dd>
      </div>
    </dl>
  );
}

function SituacaoDaDivergencia({ divergencia }: { readonly divergencia: DivergenciaDeSaldo }) {
  return (
    <span className="saldos__produto">
      <ChipStatus
        tom={tomDoStatus(divergencia.status)}
        rotulo={rotuloDoStatus(divergencia.status)}
        tamanho="tabela"
      />
      {/* A exclusão no ERP é lógica: o saldo continua aqui, marcado inativo. */}
      {!divergencia.saldoAtivo && <span className="saldos__discreto">Excluído no ERP</span>}
    </span>
  );
}

/**
 * Divergências de saldo com o ERP (r1-sync).
 *
 * A sincronização lê o saldo do Senior por filial + depósito + produto. Saldo
 * que já existia aqui e veio DIFERENTE nunca é sobrescrito sozinho: espera
 * nesta tela até alguém decidir — `Atualizar` aplica o do ERP, `Manter`
 * descarta a leitura. A tela abre nas pendentes, da mais antiga para a mais
 * nova; o filtro de situação mostra também o histórico das decididas, com
 * quem decidiu e quando.
 *
 * As ações só existem para quem decide a divergência da movimentação em duas
 * etapas (Administrador e Supervisor, `operacoes.agir`) — a mesma lista de
 * perfis da API. O perfil Consulta lê.
 */
export function DivergenciasDeSaldo() {
  const pode = usePode();
  const pendencias = usePendenciasDeSaldo();
  // O link "Decidir a divergência" da tela de Sincronização chega com o
  // recorte do saldo na URL; ela só semeia os filtros, que seguem editáveis.
  const [parametros] = useSearchParams();

  const [situacao, definirSituacao] = useState<string>(STATUS_PADRAO);
  const [produto, definirProduto] = useState(() => parametros.get('produto') ?? '');
  const [derivacao, definirDerivacao] = useState('');
  const [filial, definirFilial] = useState(() => parametros.get('codFil') ?? '');
  const [deposito, definirDeposito] = useState(() => parametros.get('deposito') ?? '');
  const [pagina, definirPagina] = useState(1);
  const [pedido, definirPedido] = useState<Pedido | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);

  const produtoAdiado = useValorAdiado(produto);
  const derivacaoAdiada = useValorAdiado(derivacao);
  const depositoAdiado = useValorAdiado(deposito);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeDivergenciasDeSaldo>(
    () => ({
      pagina,
      ...seInformado('status', statusDoFiltro(situacao)),
      ...seInformado('produto', produtoAdiado),
      ...seInformado('derivacao', derivacaoAdiada),
      ...seInformado('codFil', codFil),
      ...seInformado('deposito', depositoAdiado),
    }),
    [pagina, situacao, produtoAdiado, derivacaoAdiada, codFil, depositoAdiado],
  );

  const buscar = useCallback(() => listarDivergenciasDeSaldo(filtro), [filtro]);
  const consulta = useConsulta(buscar);
  const ultimaPagina = consulta.dados === null ? null : totalDePaginas(consulta.dados);

  // A decisão tira a divergência das pendentes: a página aberta pode esvaziar
  // com outras ainda esperando nas anteriores. "Nenhuma" ali seria falso — a
  // tela volta para a última página que existe.
  useEffect(() => {
    if (ultimaPagina !== null && pagina > ultimaPagina) {
      definirPagina(ultimaPagina);
    }
  }, [pagina, ultimaPagina]);

  /** Trocar o recorte volta para a primeira página. */
  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  function decidido(decidida: DivergenciaDeSaldo) {
    definirPedido(null);
    definirAviso(avisoDaDecisao(decidida));
    consulta.recarregar();
    pendencias.recarregar();
  }

  function acoesDa(divergencia: DivergenciaDeSaldo) {
    if (divergencia.status !== 'Pendente') {
      return (
        <span className="saldos__decisao">
          <span>{divergencia.decididoPor ?? '—'}</span>
          <span className="saldos__discreto">{formatarDataHora(divergencia.decididoEm)}</span>
        </span>
      );
    }

    if (!pode('operacoes.agir')) {
      return <span className="saldos__discreto">Decisão de Administrador ou Supervisor.</span>;
    }

    return (
      <span className="saldos__acoes">
        {(['Manter', 'Atualizar'] as const).map((decisao) => (
          <Botao
            key={decisao}
            variante={decisao === 'Manter' ? 'secundario' : 'atencao'}
            altura="pequena"
            onClick={() => {
              definirAviso(null);
              definirPedido({ divergencia, decisao });
            }}
          >
            {rotuloDaDecisaoDeSaldo(divergencia, decisao)}
          </Botao>
        ))}
      </span>
    );
  }

  const colunas: readonly Coluna<DivergenciaDeSaldo>[] = [
    {
      chave: 'produto',
      rotulo: 'Produto',
      largura: '20%',
      celula: (divergencia) => <ProdutoDaDivergencia divergencia={divergencia} />,
    },
    {
      chave: 'local',
      rotulo: 'Local',
      largura: '13%',
      celula: (divergencia) => <LocalDaDivergencia divergencia={divergencia} />,
    },
    {
      chave: 'painel',
      rotulo: 'No painel',
      largura: '86px',
      alinhamento: 'direita',
      celula: (divergencia) => (
        <Quantidade rotulo="No painel" valor={divergencia.quantidadeDisponivelLocal} />
      ),
    },
    {
      chave: 'erp',
      rotulo: 'No ERP',
      largura: '86px',
      alinhamento: 'direita',
      celula: (divergencia) => (
        <Quantidade rotulo="No ERP" valor={divergencia.quantidadeDisponivelErp} destaque />
      ),
    },
    {
      chave: 'diferenca',
      rotulo: 'Diferença',
      largura: '86px',
      alinhamento: 'direita',
      celula: (divergencia) => (
        <span className="quantidade" aria-label="Diferença">
          {formatarDiferenca(divergencia.diferenca)}
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      largura: '150px',
      celula: (divergencia) => <SituacaoDaDivergencia divergencia={divergencia} />,
    },
    {
      chave: 'datas',
      rotulo: 'Datas',
      largura: '170px',
      celula: (divergencia) => <DatasDaDivergencia divergencia={divergencia} />,
    },
    {
      chave: 'decisao',
      rotulo: 'Decisão',
      celula: acoesDa,
    },
  ];

  return (
    <Pagina
      contexto="Saldos"
      titulo="Divergências de saldo"
      faixa={
        <p className="saldos__explicacao">
          Saldo lido do ERP diferente do que está no painel. Nada muda sozinho: atualizar aplica o
          do ERP, manter descarta a leitura.
        </p>
      }
    >
      <div className="saldos">
        <FaixaDeFiltros rotulo="Filtros das divergências de saldo">
          <FiltroSelecao
            rotulo="Situação"
            valor={situacao}
            opcoes={OPCOES_DE_STATUS}
            aoMudar={(valor) => {
              recortar(() => {
                definirSituacao(valor);
              });
            }}
          />
          <CampoBusca
            rotulo="Buscar por produto"
            valor={produto}
            largura={200}
            aoMudar={(valor) => {
              recortar(() => {
                definirProduto(valor);
              });
            }}
          />
          <CampoBusca
            rotulo="Derivação"
            valor={derivacao}
            largura={130}
            aoMudar={(valor) => {
              recortar(() => {
                definirDerivacao(valor);
              });
            }}
          />
          <CampoFilial
            valor={filial}
            aoMudar={(valor) => {
              recortar(() => {
                definirFilial(valor);
              });
            }}
          />
          <CampoBusca
            rotulo="Buscar por depósito"
            valor={deposito}
            largura={180}
            aoMudar={(valor) => {
              recortar(() => {
                definirDeposito(valor);
              });
            }}
          />
          {consulta.dados !== null && (
            <ContadorRegistros>
              {contagemDoRecorte(consulta.dados.total, 'divergência', 'divergências', 'Nenhuma')}
            </ContadorRegistros>
          )}
        </FaixaDeFiltros>

        {aviso !== null && <AvisoDaTela tom="ok">{aviso}</AvisoDaTela>}

        <Resultado
          consulta={consulta}
          espera="Carregando as divergências de saldo…"
          estaVazio={(dados) => dados.itens.length === 0}
          vazio={
            <EstadoVazio
              icone={<IconeConfere tamanho={28} />}
              titulo="Nenhuma divergência de saldo neste recorte."
              detalhe="Quando o saldo lido do ERP vier diferente do que está aqui, ele espera nesta tela até alguém decidir."
            />
          }
        >
          {(dados) => (
            <Tabela
              itens={dados.itens}
              colunas={colunas}
              chaveDe={(divergencia) => divergencia.id}
              descricao="Divergências de saldo"
              classeDaLinha={classeDaLinha}
              rodape={
                <Paginacao
                  intervalo={intervaloDaPagina(dados, 'divergência', 'divergências')}
                  pagina={dados.pagina}
                  totalPaginas={totalDePaginas(dados)}
                  aoIrPara={definirPagina}
                />
              }
            />
          )}
        </Resultado>
      </div>

      {pedido !== null && (
        <DialogoDeDecisaoDeSaldo
          divergencia={pedido.divergencia}
          decisao={pedido.decisao}
          aoDecidir={decidido}
          aoCancelar={() => {
            definirPedido(null);
          }}
        />
      )}
    </Pagina>
  );
}
